import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

// Completed-trip history + utilisation for one driver OR one vehicle.
// Usage: <TripHistoryModal open entity={{ type: 'driver'|'vehicle', id, title, subtitle }} operatorId country onClose />

const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtKm = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} km`;

const PRESETS = [
    { key: 'all', label: 'All Time' },
    { key: 'month', label: 'This Month' },
    { key: '30', label: 'Last 30 Days' },
    { key: '90', label: 'Last 90 Days' },
    { key: 'year', label: 'This Year' },
    { key: 'custom', label: 'Custom' },
];

export default function TripHistoryModal({ open, onClose, entity, operatorId, country }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [preset, setPreset] = useState('all');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const PAGE = 10;

    const range = useMemo(() => {
        const now = new Date();
        if (preset === 'month') return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: ymd(now) };
        if (preset === '30') return { from: ymd(new Date(now - 29 * 86400000)), to: ymd(now) };
        if (preset === '90') return { from: ymd(new Date(now - 89 * 86400000)), to: ymd(now) };
        if (preset === 'year') return { from: ymd(new Date(now.getFullYear(), 0, 1)), to: ymd(now) };
        if (preset === 'custom') return { from, to };
        return { from: '', to: '' };
    }, [preset, from, to]);

    useEffect(() => {
        if (!open || !entity?.id) return;
        let cancelled = false;
        (async () => {
            setLoading(true); setError('');
            try {
                const params = { [entity.type === 'vehicle' ? 'vehicleId' : 'driverId']: entity.id };
                if (range.from) params.from = range.from;
                if (range.to) params.to = range.to;
                const res = await axios.get(`/api/trip-history/${operatorId || 'all'}`, { params });
                if (!cancelled) { setRows(Array.isArray(res.data) ? res.data : []); setPage(1); }
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.message || 'Could not load trip history');
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [open, entity?.id, entity?.type, operatorId, range.from, range.to]);

    useEffect(() => { if (!open) { setPreset('all'); setFrom(''); setTo(''); setSearch(''); } }, [open]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r => [r.tripName, r.client, r.route, r.pickup, r.drop, r.driver?.name, r.driver?.code, r.vehicle?.plate, r.vehicle?.code, r.kind]
            .join(' ').toLowerCase().includes(q));
    }, [rows, search]);

    // ---- Utilisation summary ----
    const stats = useMemo(() => {
        const total = filtered.length;
        const distance = filtered.reduce((s, r) => s + (Number(r.distance) || 0), 0);
        const revenue = filtered.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        const passengers = filtered.reduce((s, r) => s + (Number(r.passengers) || 0), 0);
        const days = new Set(filtered.map(r => ymd(r.date)));
        let rangeDays = 0;
        if (total) {
            const start = range.from ? new Date(range.from) : new Date(Math.min(...filtered.map(r => new Date(r.date))));
            const end = range.to ? new Date(range.to) : new Date();
            rangeDays = Math.max(1, Math.round((end - start) / 86400000) + 1);
        }
        const utilisation = rangeDays ? Math.min(100, Math.round((days.size / rangeDays) * 100)) : 0;
        const byMonth = {};
        filtered.forEach(r => {
            const d = new Date(r.date); const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            byMonth[k] = byMonth[k] || { trips: 0, km: 0 };
            byMonth[k].trips += 1; byMonth[k].km += Number(r.distance) || 0;
        });
        const months = Object.keys(byMonth).sort().slice(-6).map(k => ({
            key: k, label: new Date(k + '-01').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }), ...byMonth[k]
        }));
        const count = (fn) => {
            const m = {}; filtered.forEach(r => { const k = fn(r); if (k) m[k] = (m[k] || 0) + 1; });
            return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 4);
        };
        const partners = entity?.type === 'vehicle'
            ? count(r => r.driver ? `${r.driver.name}${r.driver.code ? ` (${r.driver.code})` : ''}` : null)
            : count(r => r.vehicle ? `${r.vehicle.code ? r.vehicle.code + ' · ' : ''}${r.vehicle.plate || ''}` : null);
        const clients = count(r => r.client || null);
        return {
            total, distance, revenue, passengers, activeDays: days.size, rangeDays, utilisation, months, partners, clients,
            avgKm: total ? distance / total : 0,
            first: total ? filtered[filtered.length - 1].date : null, last: total ? filtered[0].date : null
        };
    }, [filtered, range.from, range.to, entity?.type]);

    if (!open) return null;

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
    const pageRows = filtered.slice((page - 1) * PAGE, page * PAGE);
    const maxTrips = Math.max(1, ...stats.months.map(m => m.trips));
    const money = (n) => formatCurrency(n, country);

    const exportCSV = () => {
        const head = ['Date', 'Trip', 'Type', 'Client', 'Route', 'Pickup', 'Drop', 'Start', 'End', 'Driver', 'Vehicle', 'Start Odo', 'End Odo', 'Distance (km)', 'Passengers', 'Amount'];
        const lines = filtered.map(r => [
            fmtDate(r.date), r.tripName, r.kind, r.client, r.route, r.pickup, r.drop, r.startTime, r.endTime,
            r.driver ? `${r.driver.name}${r.driver.code ? ` (${r.driver.code})` : ''}` : '',
            r.vehicle ? `${r.vehicle.code ? r.vehicle.code + ' ' : ''}${r.vehicle.plate || ''}` : '',
            r.startOdometer ?? '', r.endOdometer ?? '', r.distance || 0, r.passengers || 0, r.amount || 0
        ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `trip_history_${(entity?.title || entity?.type || 'export').replace(/[^\w-]+/g, '_')}_${ymd(new Date())}.csv`;
        a.click(); URL.revokeObjectURL(a.href);
    };

    const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px' };
    const lbl = { fontSize: 10.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
    const val = { fontSize: 20, fontWeight: 800, color: '#0f172a', marginTop: 4 };
    const th = { textAlign: 'left', padding: '10px 12px', fontSize: 10.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
    const td = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 12.5, color: '#334155', verticalAlign: 'top' };
    const sub = { fontSize: 11, color: '#64748b', marginTop: 2 };
    const chip = (active) => ({ padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1px solid ${active ? '#1e40af' : '#e2e8f0'}`, background: active ? '#1e40af' : 'white', color: active ? 'white' : '#475569' });
    const isVeh = entity?.type === 'vehicle';

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#f8fafc', borderRadius: 18, width: '100%', maxWidth: 1150, maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>
                <div style={{ padding: '18px 24px', background: 'white', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{isVeh ? 'Vehicle' : 'Driver'} Trip History</div>
                        <h3 style={{ margin: '2px 0 0', fontSize: 19, fontWeight: 800, color: '#0f172a' }}>{entity?.title}</h3>
                        {entity?.subtitle && <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>{entity.subtitle}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={exportCSV} disabled={!filtered.length} style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #bbf7d0', background: 'white', color: '#16a34a', fontWeight: 700, fontSize: 13, cursor: filtered.length ? 'pointer' : 'not-allowed', opacity: filtered.length ? 1 : 0.5 }}>Export</button>
                        <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 20, color: '#64748b' }}>×</button>
                    </div>
                </div>

                <div style={{ padding: 20, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                        {PRESETS.map(p => <button key={p.key} style={chip(preset === p.key)} onClick={() => setPreset(p.key)}>{p.label}</button>)}
                        {preset === 'custom' && (
                            <>
                                <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ height: 32, border: '1px solid #e2e8f0', borderRadius: 8, padding: '0 8px', fontSize: 12 }} />
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>to</span>
                                <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ height: 32, border: '1px solid #e2e8f0', borderRadius: 8, padding: '0 8px', fontSize: 12 }} />
                            </>
                        )}
                        <input placeholder="Search trips, client, route…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                            style={{ marginLeft: 'auto', height: 34, minWidth: 220, border: '1px solid #e2e8f0', borderRadius: 10, padding: '0 12px', fontSize: 13, background: 'white' }} />
                    </div>

                    {error && <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#b91c1c', fontSize: 13, marginBottom: 14 }}>{error}</div>}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 14 }}>
                        <div style={card}><div style={lbl}>Completed Trips</div><div style={val}>{stats.total}</div><div style={sub}>{stats.first ? `${fmtDate(stats.first)} – ${fmtDate(stats.last)}` : 'No trips yet'}</div></div>
                        <div style={card}><div style={lbl}>Distance Covered</div><div style={val}>{fmtKm(stats.distance)}</div><div style={sub}>Avg {fmtKm(stats.avgKm)} / trip</div></div>
                        <div style={card}><div style={lbl}>Trip Revenue</div><div style={{ ...val, fontSize: 18 }}>{money(stats.revenue)}</div><div style={sub}>Excl. monthly-billed trips</div></div>
                        <div style={card}><div style={lbl}>Active Days</div><div style={val}>{stats.activeDays}</div><div style={sub}>{stats.rangeDays ? `of ${stats.rangeDays} day${stats.rangeDays > 1 ? 's' : ''} in period` : '—'}</div></div>
                        <div style={card}>
                            <div style={lbl}>Utilisation</div>
                            <div style={{ ...val, color: stats.utilisation >= 70 ? '#059669' : stats.utilisation >= 40 ? '#d97706' : '#dc2626' }}>{stats.utilisation}%</div>
                            <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99, marginTop: 6, overflow: 'hidden' }}>
                                <div style={{ width: `${stats.utilisation}%`, height: '100%', background: stats.utilisation >= 70 ? '#10b981' : stats.utilisation >= 40 ? '#f59e0b' : '#ef4444' }} />
                            </div>
                        </div>
                        <div style={card}><div style={lbl}>Passengers</div><div style={val}>{stats.passengers}</div><div style={sub}>Boarded / present</div></div>
                    </div>

                    {stats.total > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 16 }}>
                            <div style={card}>
                                <div style={{ ...lbl, marginBottom: 10 }}>Trips per Month</div>
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 110 }}>
                                    {stats.months.map(m => (
                                        <div key={m.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }} title={`${m.trips} trips · ${fmtKm(m.km)}`}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>{m.trips}</div>
                                            <div style={{ width: '100%', maxWidth: 34, height: `${Math.max(4, (m.trips / maxTrips) * 72)}px`, background: '#2563eb', borderRadius: '6px 6px 2px 2px' }} />
                                            <div style={{ fontSize: 10.5, color: '#64748b' }}>{m.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {[[isVeh ? 'Top Drivers' : 'Vehicles Driven', stats.partners], ['Top Clients', stats.clients]].map(([title, list]) => (
                                <div key={title} style={card}>
                                    <div style={{ ...lbl, marginBottom: 8 }}>{title}</div>
                                    {list.length ? list.map(([name, n]) => (
                                        <div key={name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '5px 0', borderBottom: '1px dashed #f1f5f9' }}>
                                            <span style={{ color: '#334155', fontWeight: 600 }}>{name}</span><span style={{ color: '#2563eb', fontWeight: 800 }}>{n} trip{n > 1 ? 's' : ''}</span>
                                        </div>
                                    )) : <div style={sub}>—</div>}
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        {['Date', 'Trip', 'Route', isVeh ? 'Driver' : 'Vehicle', 'Time', 'Odometer', 'Distance', 'Pax', 'Amount'].map(h => <th key={h} style={th}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={9} style={{ ...td, textAlign: 'center', padding: 30, color: '#94a3b8' }}>Loading trip history…</td></tr>
                                    ) : pageRows.length === 0 ? (
                                        <tr><td colSpan={9} style={{ ...td, textAlign: 'center', padding: 30, color: '#94a3b8' }}>No completed trips found for this period.</td></tr>
                                    ) : pageRows.map(r => (
                                        <tr key={r._id}>
                                            <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{fmtDate(r.date)}</td>
                                            <td style={td}>
                                                <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.tripName || r.client || 'Trip'}</div>
                                                <div style={sub}>{[r.client && r.tripName ? r.client : null, r.tripType, r.kind].filter(Boolean).join(' · ')}</div>
                                            </td>
                                            <td style={{ ...td, maxWidth: 260 }}>
                                                {r.route && <div style={{ fontWeight: 600 }}>{r.route}</div>}
                                                <div style={{ ...sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${r.pickup} → ${r.drop}`}>{r.pickup} → {r.drop}</div>
                                            </td>
                                            <td style={td}>
                                                {isVeh
                                                    ? (r.driver ? <><div style={{ fontWeight: 600 }}>{r.driver.name}</div>{r.driver.code && <div style={sub}>{r.driver.code}</div>}</> : '—')
                                                    : (r.vehicle ? <><div style={{ fontWeight: 600 }}>{r.vehicle.plate}</div><div style={sub}>{[r.vehicle.code, r.vehicle.model].filter(Boolean).join(' · ')}</div></> : '—')}
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.startTime || '—'}{r.endTime ? ` – ${r.endTime}` : ''}</td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.startOdometer != null || r.endOdometer != null ? `${r.startOdometer ?? '—'} → ${r.endOdometer ?? '—'}` : '—'}</td>
                                            <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{r.distance ? fmtKm(r.distance) : '—'}</td>
                                            <td style={td}>{r.passengers || '—'}</td>
                                            <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{r.amount ? money(r.amount) : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {filtered.length > PAGE && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid #f1f5f9', fontSize: 12.5, color: '#64748b' }}>
                                <span>Showing {(page - 1) * PAGE + 1}–{Math.min(page * PAGE, filtered.length)} of {filtered.length}</span>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ ...chip(false), opacity: page === 1 ? 0.5 : 1 }}>Prev</button>
                                    <span>{page} / {totalPages}</span>
                                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{ ...chip(false), opacity: page === totalPages ? 0.5 : 1 }}>Next</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
